import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle, Crown } from "lucide-react";

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  // Stripe redirects with ?session_id=..., Flutterwave with ?tx_ref=...&transaction_id=...&status=...
  const sessionId = params.get("session_id");
  const txRef = params.get("tx_ref");
  const txId = params.get("transaction_id");
  const flwStatus = params.get("status"); // 'successful' | 'cancelled' | 'failed'
  const { refreshMe } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId && !txRef) {
      setState("error");
      return;
    }
    // Flutterwave cancelled/failed → straight to error
    if (txRef && flwStatus && flwStatus !== "successful" && flwStatus !== "completed") {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) {
        setState("error");
        return;
      }
      attempts.current += 1;
      try {
        let data;
        if (txRef) {
          ({ data } = await api.get(`/billing/flutterwave/verify`, {
            params: { tx_ref: txRef, transaction_id: txId },
          }));
        } else {
          ({ data } = await api.get(`/billing/status/${sessionId}`));
        }
        if (data.payment_status === "paid") {
          await refreshMe();
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, txRef, txId, flwStatus, refreshMe]);

  return (
    <div className="p-6 md:p-10 max-w-xl mx-auto flex items-center min-h-[70vh]">
      <Card className="p-10 bg-af-card border-af rounded-md text-center w-full">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="h-12 w-12 text-terracotta mx-auto mb-5 animate-spin" />
            <h1 className="font-display font-extrabold text-2xl mb-2">Vérification du paiement…</h1>
            <p className="text-af-secondary text-sm">Merci de patienter quelques secondes.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-success">
            <div className="h-16 w-16 rounded-full bg-[#F5EFD8] border border-af flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-terracotta" />
            </div>
            <h1 className="font-display font-extrabold text-2xl mb-2 flex items-center justify-center gap-2">
              <Crown className="h-5 w-5 text-terracotta" /> Bienvenue dans Business !
            </h1>
            <p className="text-af-secondary text-sm mb-8">
              Votre paiement de 35 000 FCFA a été confirmé. Vous profitez désormais de réunions illimitées.
            </p>
            <Link to="/app/meetings">
              <Button className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md h-11 px-6" data-testid="payment-success-cta">
                Créer une réunion
              </Button>
            </Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <div className="h-16 w-16 rounded-full bg-[#FFE4E6] border border-[#FECDD3] flex items-center justify-center mx-auto mb-5">
              <XCircle className="h-8 w-8 text-[#9F1239]" />
            </div>
            <h1 className="font-display font-extrabold text-2xl mb-2">Paiement non confirmé</h1>
            <p className="text-af-secondary text-sm mb-8">
              Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez le support — sinon, réessayez.
            </p>
            <Link to="/app/billing">
              <Button variant="outline" className="border-af rounded-md h-11 px-6" data-testid="payment-retry-btn">
                Retour à l'abonnement
              </Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
